import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileUploadArea } from '@/components/course/FileUploadArea';
import { Upload, FileSpreadsheet, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { trackNeedsAssessment } from '@/utils/googleAnalytics';

interface AssessmentResult {
  needsMet: Array<{ need: string; examples: Array<{ title: string; link: string }> }>;
  needsExceeded: Array<{ need: string; examples: Array<{ title: string; link: string }> }>;
  inDevelopment: Array<{ need: string; timeline: string }>;
  overallScore: number;
  summary: string;
}

export const DistrictNeedsAssessment: React.FC = () => {
  const [textInput, setTextInput] = useState('');
  const [csvData, setCsvData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [showResults, setShowResults] = useState(false);

  const handleFileUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                   file.type === 'application/vnd.ms-excel' ||
                   file.name.endsWith('.xlsx') || 
                   file.name.endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    if (isCSV) {
      // Handle CSV files
      Papa.parse(file, {
        complete: (results) => {
          setCsvData(JSON.stringify(results.data));
          toast.success('CSV file uploaded successfully');
        },
        header: true,
        error: (error) => {
          toast.error('Error parsing CSV file');
          console.error('CSV parse error:', error);
        }
      });
    } else if (isExcel) {
      // Handle Excel files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          setCsvData(JSON.stringify(jsonData));
          toast.success('Excel file uploaded successfully');
        } catch (error) {
          toast.error('Error parsing Excel file');
          console.error('Excel parse error:', error);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleAnalysis = async () => {
    if (!textInput.trim() && !csvData) {
      toast.error('Please enter requirements or upload a CSV file');
      return;
    }

    setLoading(true);
    const inputType = csvData ? 'file' : 'text';
    
    try {
      const { data, error } = await supabase.functions.invoke('analyze-district-needs', {
        body: {
          textInput: textInput.trim(),
          csvData: csvData
        }
      });

      if (error) throw error;

      setResult(data.analysis);
      setShowResults(true);
      toast.success('Analysis complete!');
      
      // Track successful needs assessment
      trackNeedsAssessment(inputType, true);
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze requirements. Please try again.');
      
      // Track failed needs assessment
      trackNeedsAssessment(inputType, false);
    } finally {
      setLoading(false);
    }
  };

  const downloadWordDoc = async () => {
    if (!result) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-needs-report', {
        body: { result }
      });

      if (error) throw error;

      // Create and download the Word document with proper formatting
      const blob = new Blob([data.document], { 
        type: 'application/msword' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || 'district-needs-assessment.doc';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to generate report. Please try again.');
    }
  };

  if (showResults && result) {
    return (
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-primary" />
                District Needs Assessment Results
              </CardTitle>
              <CardDescription>
                AI-powered analysis of your professional development requirements
              </CardDescription>
            </div>
            <Button onClick={downloadWordDoc} className="gap-2">
              <Download className="h-4 w-4" />
              Download Report
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Overall Score */}
          <div className="text-center p-6 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
            <div className="text-4xl font-bold text-primary mb-2">{result.overallScore}%</div>
            <p className="text-lg font-medium">Platform Match Score</p>
            <p className="text-muted-foreground mt-2">{result.summary}</p>
          </div>

          {/* Needs Met */}
          {result.needsMet.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4 flex items-center gap-2">
                ✅ Needs Met ({result.needsMet.length})
              </h3>
              <div className="grid gap-4">
                {result.needsMet.map((item, index) => (
                  <Card key={index} className="border-green-200 bg-green-50/50">
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">{item.need}</h4>
                      <div className="space-y-1">
                        {item.examples.map((example, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>•</span>
                            <span>{example.title}</span>
                            <span className="text-xs bg-primary/10 px-2 py-1 rounded">Available after login</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Needs Exceeded */}
          {result.needsExceeded.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4 flex items-center gap-2">
                🚀 Needs Exceeded ({result.needsExceeded.length})
              </h3>
              <div className="grid gap-4">
                {result.needsExceeded.map((item, index) => (
                  <Card key={index} className="border-blue-200 bg-blue-50/50">
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">{item.need}</h4>
                      <div className="space-y-1">
                        {item.examples.map((example, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>•</span>
                            <span>{example.title}</span>
                            <span className="text-xs bg-primary/10 px-2 py-1 rounded">Available after login</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* In Development */}
          {result.inDevelopment.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-orange-600 mb-4 flex items-center gap-2">
                🔨 In Development ({result.inDevelopment.length})
              </h3>
              <div className="grid gap-4">
                {result.inDevelopment.map((item, index) => (
                  <Card key={index} className="border-orange-200 bg-orange-50/50">
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-1">{item.need}</h4>
                      <p className="text-sm text-muted-foreground">Timeline: {item.timeline}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button 
              onClick={() => setShowResults(false)}
              variant="outline"
              className="flex-1"
            >
              New Assessment
            </Button>
            <Button 
              onClick={() => window.location.href = '/auth'}
              className="flex-1"
            >
              Get Started with Edugogy
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          District Needs Assessment
        </CardTitle>
        <CardDescription>
          Upload your district's professional development requirements and get an AI-powered analysis of how Edugogy can meet your needs
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <FileSpreadsheet className="h-4 w-4" />
          <AlertDescription>
            <strong>Two ways to submit your requirements:</strong>
            <br />
            1. Upload a CSV or Excel file with your needs/requirements
            <br />
            2. Enter requirements as text below
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div>
            <Label htmlFor="file-upload" className="text-base font-medium">
              Option 1: Upload CSV or Excel File
            </Label>
            <p className="text-sm text-muted-foreground mb-3">
              Upload a CSV or Excel file containing your district's professional development needs
            </p>
            <FileUploadArea
              onFilesSelected={handleFileUpload}
              acceptedFileTypes=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              maxFiles={1}
              multiple={false}
            />
            {csvData && (
              <Alert className="mt-2 bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">
                  ✅ File uploaded and processed successfully
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">OR</span>
            </div>
          </div>

          <div>
            <Label htmlFor="text-input" className="text-base font-medium">
              Option 2: Enter Requirements as Text
            </Label>
            <p className="text-sm text-muted-foreground mb-3">
              Describe your district's professional development needs and requirements
            </p>
            <Textarea
              id="text-input"
              placeholder="Example: We need courses for new teacher mentoring, technology integration for K-12, classroom management strategies, special education compliance training, and STEM curriculum development..."
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows={6}
              className="resize-none"
            />
          </div>
        </div>

        <Button 
          onClick={handleAnalysis} 
          disabled={loading || (!textInput.trim() && !csvData)}
          className="w-full text-lg py-6"
          size="lg"
        >
          {loading ? (
            <>
              <Upload className="mr-2 h-5 w-5 animate-spin" />
              Analyzing Requirements...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-5 w-5" />
              Analyze District Needs
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};